import api from '@/api_alv'
import i18n from '@/assets/i18n/i18n'

const DIR = 'publishing/statistics'

const WORK_TYPE_LABELS = {
    work: 'lang_GENERAL_WORK',
    opw: 'lang_PUBLISHING_WORKS_DROPDOWN_TYPE_OPW',
    spw: 'lang_PUBLISHING_WORKS_DROPDOWN_TYPE_SPW',
    upw: 'lang_PUBLISHING_WORKS_DROPDOWN_TYPE_UPW',
}

const RIGHTS_TYPE_LABELS = {
    pr: 'lang_PUBLISHING_CAPTION_PERFORMING_RIGHTS_ABBREVIATION',
    mr: 'lang_PUBLISHING_CAPTION_MECHANICAL_RIGHTS_ABBREVIATION'
}

const CHART_TITLES = {
    bySources: { text: ['By Sources'], value: 'sources' },
    byType: { text: ['By Type'], value: 'type' },
    byRightsType: { text: ['By Rights Type'], value: 'rightsType' },
    byLicensor: { text: ['By Licensor'], value: 'byLicensor' },
    byWork: { text: ['By Work'], value: 'byWork' },
}

const LICENSOR_CHART_KEYS = ['byLicensor', 'byWork', 'byRightsType']
const PIE_SLICE_LIMIT = 10

function monthToQuarterYear(monthStr) {
    if (!monthStr) return null
    const [year, month] = monthStr.split('-').map(Number)
    return { year, quarter: Math.ceil(month / 3) }
}

function getPeriodApiFields(fromMonth, toMonth) {
    const fields = {}
    if (fromMonth) fields.fromMonth = fromMonth
    if (toMonth) fields.toMonth = toMonth

    const from = monthToQuarterYear(fromMonth)
    const to = monthToQuarterYear(toMonth)
    if (from) {
        fields.fromQuarter = from.quarter
        fields.fromYear = from.year
    }
    if (to) {
        fields.toQuarter = to.quarter
        fields.toYear = to.year
    }
    return fields
}

/** Legacy favorites may store byDistributors; backend key is byType. */
function normalizeChartKey(chartKey) {
    if (chartKey === 'byDistributors') return 'byType'
    return chartKey
}

function resolveChartLabel(chartKey, key, rootGetters) {
    if (key == null || key === '') return ''

    if (chartKey === 'bySources') {
        const azSources = rootGetters.getAzForMappingsImport || []
        const match = azSources.find((source) => (
            source.alvId === key
            || source.value === key
            || String(source.id) === String(key)
        ))
        return match ? match.text : key
    }

    if (chartKey === 'byType') {
        const label = WORK_TYPE_LABELS[String(key).toLowerCase()]
        return label || key
    }

    if (chartKey === 'byRightsType') {
        const label = RIGHTS_TYPE_LABELS[String(key).toLowerCase()]
        return label || key
    }

    return key
}

function mapBackendChartToPiePoints(entries, chartKey, rootGetters) {
    if (!Array.isArray(entries) || entries.length === 0) return []

    return entries
        .map((entry) => ({
            name: resolveChartLabel(chartKey, entry.key, rootGetters),
            y: Number(entry.amount) || 0,
            q: Number(entry.work_count) || 0,
        }))
        .filter((point) => point.y !== 0)
}

function buildChartFromBackend(charts, chartKey, rootGetters) {
    const normalizedKey = normalizeChartKey(chartKey)
    const title = CHART_TITLES[normalizedKey] || CHART_TITLES.bySources
    const entries = charts && charts[normalizedKey] ? charts[normalizedKey] : []

    return {
        title,
        details: mapBackendChartToPiePoints(entries, normalizedKey, rootGetters),
    }
}

function mapBackendCards(cards) {
    if (!cards) {
        return {
            firstCard: { text: 'lang_PUBLISHING_STATISTICS_CAPTION_REVENUE', value: 0 },
            secondCard: { text: 'lang_GENERIC_CAPTION_DOMESTIC', value: 0 },
            thirdCard: { text: 'lang_GENERIC_CAPTION_FOREIGN', value: 0 },
            fourthCard: { text: 'lang_GENERIC_CAPTION_LICENSING_REVENUES', value: 0 },
        }
    }

    return {
        firstCard: { text: 'lang_PUBLISHING_STATISTICS_CAPTION_REVENUE', value: Number(cards.sum) || 0 },
        secondCard: { text: 'lang_GENERIC_CAPTION_DOMESTIC', value: Number(cards.domestic) || 0 },
        thirdCard: { text: 'lang_GENERIC_CAPTION_FOREIGN', value: Number(cards.foreign) || 0 },
        fourthCard: { text: 'lang_GENERIC_CAPTION_LICENSING_REVENUES', value: Number(cards.licensing) || 0 },
    }
}

/**
 * Datatable from list; pie charts + cards from backend.
 */
function buildPublishingStatisticsDisplay(list, charts, cards, filters, rootGetters) {
    const leftChart = buildChartFromBackend(charts, filters.leftChart, rootGetters)
    const rightChart = buildChartFromBackend(charts, filters.rightChart, rootGetters)
    const mappedCards = mapBackendCards(cards)

    const sortedWorks = [...list]
        .sort((a, b) => (Number(b.amountTotal) || 0) - (Number(a.amountTotal) || 0))
        .slice(0, 100)
        .map((work) => ({
            statusType: work.type,
            title: work.title,
            composers: Array.isArray(work.composers) ? work.composers.join(' / ') : work.composers,
            gemaNumber: work.gemaNumber,
            amountDomestic: work.amountDomestic,
            amountForeign: work.amountForeign,
            amountTotal: work.amountTotal,
            alvId: work.alvId,
        }))

    const currency = rootGetters.getSelectedCurrency
    const homeSociety = rootGetters.getHomeSociety

    const datatable = {
        title: 'Top 100 Works',
        headers: [
            { text: 'lang_GENERIC_CAPTION_TYPE', value: 'statusType', align: 'center', sortable: true, class: 'text-no-wrap' },
            { text: 'lang_PUBLISHING_WORKS_CAPTION_INTERNATIONAL_WORK_TITLE', value: 'title', align: 'left', sortable: true, class: 'text-no-wrap' },
            { text: 'lang_PUBLISHING_CAPTION_COMPOSERS', value: 'composers', align: 'left', sortable: false, class: 'text-no-wrap' },
            { text: 'lang_PUBLISHING_WORKS_CAPTION_WORK_ID', value: 'gemaNumber', align: 'right', sortable: true, class: 'text-no-wrap' },
            { text: 'lang_GENERIC_CAPTION_DOMESTIC', value: 'amountDomestic', align: 'right', sortable: true, class: 'text-no-wrap', suffix: homeSociety },
            { text: 'lang_GENERIC_CAPTION_FOREIGN', value: 'amountForeign', align: 'right', sortable: true, class: 'text-no-wrap' },
            // { text: 'lang_GENERIC_CAPTION_LICENSING_REVENUES', value: 'amountDV', align: 'right', sortable: true, class: 'text-no-wrap' },
            { text: 'lang_GENERIC_CAPTION_SUM', value: 'amountTotal', align: 'right', sortable: true, class: 'text-no-wrap', suffix: currency },
        ],
        data: sortedWorks,
    }

    return { cards: mappedCards, leftChart, rightChart, datatable }
}

const LICENSOR_PAGE_LIMIT = 100

function monthToFromDate(monthStr) {
    if (!monthStr) return null
    return monthStr + '-01'
}

function monthToToDate(monthStr) {
    if (!monthStr) return null
    const [year, month] = monthStr.split('-').map(Number)
    if (!year || !month) return null
    const lastDay = new Date(year, month, 0).getDate()
    return monthStr + '-' + String(lastDay).padStart(2, '0')
}

function mapLicensorWork(work) {
    return {
        alvId: work.alvId,
        title: work.title,
        mr: Number(work.mr) || 0,
        pr: Number(work.pr) || 0,
        sum: Number(work.sum) || 0,
    }
}

function mapLicensorRows(licensors) {
    if (!Array.isArray(licensors)) return []
    return licensors.map((licensor) => {
        const works = Array.isArray(licensor.works) ? licensor.works.map(mapLicensorWork) : []
        works.sort((a, b) => (Number(b.sum) || 0) - (Number(a.sum) || 0))
        return {
            alvId: licensor.alvId,
            name: licensor.name,
            mr: Number(licensor.mr) || 0,
            pr: Number(licensor.pr) || 0,
            sum: Number(licensor.sum) || 0,
            works,
        }
    })
}

function getSelectedLicensorAlvId(getters) {
    const selected = getters.getSelectedPublishingStatisticsLicensor
    if (selected && selected.alvId) return selected.alvId
    const details = getters.getPublishingStatisticsDetailsLicensor
    if (details && details.licensor && details.licensor.alvId) return details.licensor.alvId
    if (details && details.alvId) return details.alvId
    return null
}

function buildLicensorRequestBody(getters, extra) {
    const body = { ...(extra || {}) }
    const from = monthToFromDate(getters.getSelectedPublishingStatisticsFrom)
    const to = monthToToDate(getters.getSelectedPublishingStatisticsTo)
    if (from) body.from = from
    if (to) body.to = to
    const search = getters.getPublishingStatisticsSearch
    if (search) body.search = search
    const licensorId = getSelectedLicensorAlvId(getters)
    if (licensorId) body.licensorId = licensorId
    return body
}

function topNWithOther(points) {
    const sorted = (points || [])
        .filter((point) => point.y !== 0)
        .sort((a, b) => b.y - a.y)
    if (sorted.length <= PIE_SLICE_LIMIT) return sorted
    const top = sorted.slice(0, PIE_SLICE_LIMIT)
    const restPoints = sorted.slice(PIE_SLICE_LIMIT)
    const restY = restPoints.reduce((sum, point) => sum + point.y, 0)
    const restQ = restPoints.reduce((sum, point) => sum + (Number(point.q) || 0), 0)
    if (restY) {
        top.push({ name: 'lang_GENERIC_CAPTION_OTHERS', y: restY, q: restQ })
    }
    return top
}

function countLicensorWorks(rows, predicate) {
    let count = 0
    ;(rows || []).forEach((row) => {
        const works = row.works || []
        if (works.length) {
            count += works.filter(predicate).length
        } else if (predicate(row)) {
            count += 1
        }
    })
    return count
}

function buildLicensorChart(rows, chartKey) {
    const key = LICENSOR_CHART_KEYS.includes(chartKey) ? chartKey : 'byLicensor'
    const title = CHART_TITLES[key] || CHART_TITLES.byLicensor
    let details = []

    if (key === 'byLicensor') {
        details = topNWithOther((rows || []).map((row) => ({
            name: row.name || row.alvId || '',
            y: Number(row.sum) || 0,
            q: (row.works || []).length,
        })))
    } else if (key === 'byWork') {
        const byWork = {}
        ;(rows || []).forEach((row) => {
            ;(row.works || []).forEach((work) => {
                const id = work.alvId || work.title
                if (!id) return
                if (!byWork[id]) {
                    byWork[id] = { name: work.title || work.alvId, y: 0, q: 0 }
                }
                byWork[id].y += Number(work.sum) || 0
                byWork[id].q += 1
            })
        })
        details = topNWithOther(Object.values(byWork))
    } else {
        const pr = (rows || []).reduce((sum, row) => sum + (Number(row.pr) || 0), 0)
        const mr = (rows || []).reduce((sum, row) => sum + (Number(row.mr) || 0), 0)
        details = [
            {
                name: 'lang_PUBLISHING_CAPTION_PERFORMING_RIGHTS_ABBREVIATION',
                y: pr,
                q: countLicensorWorks(rows, (work) => (Number(work.pr) || 0) > 0),
            },
            {
                name: 'lang_PUBLISHING_CAPTION_MECHANICAL_RIGHTS_ABBREVIATION',
                y: mr,
                q: countLicensorWorks(rows, (work) => (Number(work.mr) || 0) > 0),
            },
        ].filter((point) => point.y !== 0)
    }

    return { title, details }
}

function buildLicensorCards(rows) {
    const list = rows || []
    const sum = list.reduce((total, row) => total + (Number(row.sum) || 0), 0)
    const pr = list.reduce((total, row) => total + (Number(row.pr) || 0), 0)
    const mr = list.reduce((total, row) => total + (Number(row.mr) || 0), 0)
    return {
        firstCard: { text: 'lang_PUBLISHING_STATISTICS_CAPTION_REVENUE', value: sum },
        secondCard: { text: 'lang_PUBLISHING_CAPTION_PERFORMING_RIGHTS_ABBREVIATION', value: pr },
        thirdCard: { text: 'lang_PUBLISHING_CAPTION_MECHANICAL_RIGHTS_ABBREVIATION', value: mr },
        fourthCard: { text: 'lang_GENERIC_CAPTION_LICENSING_REVENUES', value: 0 },
    }
}

function commitLicensorDisplay(commit, getters, rows) {
    const leftChart = buildLicensorChart(rows, getters.getSelectedPublishingStatisticsLeftChart)
    const rightChart = buildLicensorChart(rows, getters.getSelectedPublishingStatisticsRightChart)

    commit('setPublishingStatisticsFirstPieChartTitle', leftChart.title)
    commit('setPublishingStatisticsFirstPieChartSeriesTitle', 'Revenue')
    commit('setPublishingStatisticsFirstPieChartSeriesData', leftChart.details || [])

    commit('setPublishingStatisticsSecondPieChartTitle', rightChart.title)
    commit('setPublishingStatisticsSecondPieChartSeriesTitle', 'Revenue')
    commit('setPublishingStatisticsSecondPieChartSeriesData', rightChart.details || [])

    commit('setPublishingStatisticsCards', buildLicensorCards(rows))
}

function buildPublishingStatisticsFilters(getters) {
    return {
        leftChart: getters.getSelectedPublishingStatisticsLeftChart,
        rightChart: getters.getSelectedPublishingStatisticsRightChart,
        sources: getters.getSelectedPublishingStatisticsSources,
        rightsType: getters.getSelectedPublishingStatisticsRightsType,
        rightsholder: getters.getSelectedPublishingStatisticsRightsholder,
        dataTable: getters.getSelectedPublishingStatisticsDataTable,
    }
}

function commitPublishingStatisticsDisplay(commit, getters, rootGetters, list, charts, cards, options) {
    const aggregated = buildPublishingStatisticsDisplay(
        list,
        charts,
        cards,
        buildPublishingStatisticsFilters(getters),
        rootGetters,
    )

    commit('setPublishingStatisticsFirstPieChartTitle', aggregated.leftChart.title)
    commit('setPublishingStatisticsFirstPieChartSeriesTitle', 'Revenue')
    commit('setPublishingStatisticsFirstPieChartSeriesData', aggregated.leftChart.details || [])

    commit('setPublishingStatisticsSecondPieChartTitle', aggregated.rightChart.title)
    commit('setPublishingStatisticsSecondPieChartSeriesTitle', 'Revenue')
    commit('setPublishingStatisticsSecondPieChartSeriesData', aggregated.rightChart.details || [])

    commit('setPublishingStatisticsCards', aggregated.cards)
    if (!options || !options.skipDatatable) {
        commit('setPublishingStatisticsDatatableData', aggregated.datatable)
    }
}

function buildPublishingStatisticsFavoritePayload(getters) {
    const rightsholder = getters.getSelectedPublishingStatisticsRightsholder
    return {
        ...getPeriodApiFields(
            getters.getSelectedPublishingStatisticsFrom,
            getters.getSelectedPublishingStatisticsTo,
        ),
        monthFrom: getters.getSelectedPublishingStatisticsFrom,
        monthTo: getters.getSelectedPublishingStatisticsTo,
        search: getters.getPublishingStatisticsSearch,
        rightsType: getters.getSelectedPublishingStatisticsRightsType,
        typeOfRights: getters.getSelectedPublishingStatisticsRightsType,
        sources: getters.getSelectedPublishingStatisticsSources,
        contracts: getters.getSelectedPublishingStatisticsContracts,
        rightsholder: rightsholder && rightsholder.alvId
            ? rightsholder.alvId
            : (rightsholder || null),
        region: getters.getSelectedPublishingStatisticsRegions,
        leftChart: normalizeChartKey(getters.getSelectedPublishingStatisticsLeftChart),
        rightChart: normalizeChartKey(getters.getSelectedPublishingStatisticsRightChart),
        dataTable: getters.getSelectedPublishingStatisticsDataTable,
        rows: getters.getSelectedPublishingStatisticsRows,
        columnTitles: getters.getSelectedPublishingStatisticsColumnTitles,
        outputType: getters.getSelectedPublishingStatisticsOutputType,
        text: getters.getPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName,
    }
}

function isStatisticsFavoriteNameTaken(dropdownValues, favoriteName) {
    const name = String(favoriteName == null ? '' : favoriteName).trim()
    if (!name) return false
    return (dropdownValues || []).some((item) => {
        if (!item || item.divider) return false
        if (item.value === 'newFavorite' || item.value === 'deleteFavorite') return false
        if (item.text == null || item.text === '') return false
        if (String(item.text).startsWith('lang_')) return false
        return String(item.text).trim() === name
    })
}

/** Resolve favorite rightsholder to A-Z entity. Prefers full ALV serial (ALV…AP/AG). */
async function resolvePublishingStatisticsRightsholder(raw) {
    if (raw == null || raw === '') return null

    const candidates = []
    if (typeof raw === 'object') {
        if (raw.alvId) candidates.push(String(raw.alvId))
    } else {
        const s = String(raw)
        if (s.startsWith('ALV')) {
            candidates.push(s)
        } else if (/^\d+$/.test(s)) {
            // Legacy DB row id — try person then group serial (cannot know type from number alone)
            const padded = s.padStart(9, '0')
            candidates.push(`ALV${padded}AP`, `ALV${padded}AG`)
        }
    }

    const isValidAzEntity = (entity) => entity && entity.error !== 1 && entity.id != null

    for (const alvId of candidates) {
        if (alvId.endsWith('AG')) {
            const group = await api.getDetailsById('az/groups', alvId)
            if (isValidAzEntity(group)) return group
            continue
        }

        const person = await api.getDetailsById('az/persons', alvId)
        if (isValidAzEntity(person)) return person
    }
    return null
}

const state = {
    publishingStatistics: [],
    publishingStatisticsSearch: '',
    selectedPublishingStatisticsRows: 'works',
    selectedPublishingStatisticsColumnTitles: 'period',
    selectedPublishingStatisticsOutputType: 'amount',
    selectedPublishingStatisticsFrom: null,
    selectedPublishingStatisticsTo: null,
    selectedPublishingStatisticsFromQuarter: 1,
    selectedPublishingStatisticsToQuarter: 4,
    selectedPublishingStatisticsFromYear: new Date().getFullYear() - 1,
    selectedPublishingStatisticsToYear: new Date().getFullYear() - 1,
    selectedPublishingStatisticsTypeOfRights: ['pr', 'mr', 'sync'],

    selectedPublishingStatisticsRightsholder: null,
    selectedPublishingStatisticsLicensor: null,
    selectedPublishingStatisticsContracts: [],
    selectedPublishingStatisticsSources: [],
    selectedPublishingStatisticsRightsType: ['pr', 'mr', 'sync'],
    selectedPublishingStatisticsLeftChart: 'bySources',
    selectedPublishingStatisticsRightChart: 'byType',
    selectedPublishingStatisticsDataTable: 'top100Works',

    selectedPublishingStatisticsRegions: {
        include: [],
        exclude: [],
    },
    selectedPublishingStatisticsRegionsString: '',

    publishingStatisticsFavoriteDropdownValues: [],
    selectedPublishingStatisticsFavorite: null,
    showPublishingStatisticsCreateNewFavoriteDialog: false,
    publishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName: null,
    tempSaveSelectedPublishingStatisticsFavorite: [],

    publishingStatisticsRightsholderOptions: [],
    publishingStatisticsDetailsRightsholder: null,
    publishingStatisticsLicensorOptions: [],
    publishingStatisticsDetailsLicensor: null,

    showPublishingStatisticsChartsSection: false,
    publishingStatisticsCharts: null,
    publishingStatisticsReportCards: null,
    publishingStatisticsFirstPieChartTitle: { text: [], value: '' },
    publishingStatisticsFirstPieChartSeriesTitle: '',
    publishingStatisticsFirstPieChartSeriesData: [],
    publishingStatisticsSecondPieChartTitle: { text: [], value: '' },
    publishingStatisticsSecondPieChartSeriesTitle: '',
    publishingStatisticsSecondPieChartSeriesData: [],
    publishingStatisticCards: {},
    publishingStatisticsDatatableData: { title: '', headers: [], data: [] },
    publishingStatisticsLicensorRows: [],
    publishingStatisticsLicensorPage: 1,
    publishingStatisticsLicensorLimit: LICENSOR_PAGE_LIMIT,
    publishingStatisticsLicensorTotal: 0,
    publishingStatisticsLicensorNumberOfPages: 0,
    publishingStatisticsLicensorHasRun: false,
    publishingStatisticsExportInProgress: false,
}

const getters = {
    getPublishingStatistics(state) { return state.publishingStatistics },
    getPublishingStatisticsSearch(state) { return state.publishingStatisticsSearch },
    getSelectedPublishingStatisticsRows(state) { return state.selectedPublishingStatisticsRows },
    getSelectedPublishingStatisticsColumnTitles(state) { return state.selectedPublishingStatisticsColumnTitles },
    getSelectedPublishingStatisticsOutputType(state) { return state.selectedPublishingStatisticsOutputType },
    getSelectedPublishingStatisticsFrom(state) { return state.selectedPublishingStatisticsFrom },
    getSelectedPublishingStatisticsTo(state) { return state.selectedPublishingStatisticsTo },
    getSelectedPublishingStatisticsFromQuarter(state) { return state.selectedPublishingStatisticsFromQuarter },
    getSelectedPublishingStatisticsToQuarter(state) { return state.selectedPublishingStatisticsToQuarter },
    getSelectedPublishingStatisticsFromYear(state) { return state.selectedPublishingStatisticsFromYear },
    getSelectedPublishingStatisticsToYear(state) { return state.selectedPublishingStatisticsToYear },
    getSelectedPublishingStatisticsTypeOfRights(state) { return state.selectedPublishingStatisticsTypeOfRights },

    getSelectedPublishingStatisticsRightsholder(state) { return state.selectedPublishingStatisticsRightsholder },
    getSelectedPublishingStatisticsLicensor(state) { return state.selectedPublishingStatisticsLicensor },
    getSelectedPublishingStatisticsContracts(state) { return state.selectedPublishingStatisticsContracts },
    getSelectedPublishingStatisticsSources(state) { return state.selectedPublishingStatisticsSources },
    getSelectedPublishingStatisticsRightsType(state) { return state.selectedPublishingStatisticsRightsType },
    getSelectedPublishingStatisticsLeftChart(state) { return state.selectedPublishingStatisticsLeftChart },
    getSelectedPublishingStatisticsRightChart(state) { return state.selectedPublishingStatisticsRightChart },
    getSelectedPublishingStatisticsDataTable(state) { return state.selectedPublishingStatisticsDataTable },

    getSelectedPublishingStatisticsRegions(state) { return state.selectedPublishingStatisticsRegions },
    getSelectedPublishingStatisticsRegionsInclude(state) { return state.selectedPublishingStatisticsRegions.include },
    getSelectedPublishingStatisticsRegionsExclude(state) { return state.selectedPublishingStatisticsRegions.exclude },
    getSelectedPublishingStatisticsRegionsString(state) { return state.selectedPublishingStatisticsRegionsString },

    getPublishingStatisticsFavoriteDropdownValues(state) { return state.publishingStatisticsFavoriteDropdownValues },
    getSelectedPublishingStatisticsFavorite(state) { return state.selectedPublishingStatisticsFavorite },
    getShowPublishingStatisticsCreateNewFavoriteDialog(state) { return state.showPublishingStatisticsCreateNewFavoriteDialog },
    getPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName(state) { return state.publishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName },
    getTempSaveSelectedPublishingStatisticsFavorite(state) { return state.tempSaveSelectedPublishingStatisticsFavorite },

    getPublishingStatisticsRightsholderOptions(state) { return state.publishingStatisticsRightsholderOptions },
    getPublishingStatisticsDetailsRightsholder(state) { return state.publishingStatisticsDetailsRightsholder },
    getPublishingStatisticsLicensorOptions(state) { return state.publishingStatisticsLicensorOptions },
    getPublishingStatisticsDetailsLicensor(state) { return state.publishingStatisticsDetailsLicensor },

    getShowPublishingStatisticsChartsSection(state) { return state.showPublishingStatisticsChartsSection },
    getPublishingStatisticsCharts(state) { return state.publishingStatisticsCharts },
    getPublishingStatisticsReportCards(state) { return state.publishingStatisticsReportCards },
    getPublishingStatisticsFirstPieChartTitle(state) { return state.publishingStatisticsFirstPieChartTitle },
    getPublishingStatisticsFirstPieChartSeriesTitle(state) { return state.publishingStatisticsFirstPieChartSeriesTitle },
    getPublishingStatisticsFirstPieChartSeriesData(state) { return state.publishingStatisticsFirstPieChartSeriesData },
    getPublishingStatisticsSecondPieChartTitle(state) { return state.publishingStatisticsSecondPieChartTitle },
    getPublishingStatisticsSecondPieChartSeriesTitle(state) { return state.publishingStatisticsSecondPieChartSeriesTitle },
    getPublishingStatisticsSecondPieChartSeriesData(state) { return state.publishingStatisticsSecondPieChartSeriesData },
    getPublishingStatisticsCards(state) { return state.publishingStatisticCards },
    getPublishingStatisticsDatatableData(state) { return state.publishingStatisticsDatatableData },
    getPublishingStatisticsLicensorRows(state) { return state.publishingStatisticsLicensorRows },
    getPublishingStatisticsLicensorPage(state) { return state.publishingStatisticsLicensorPage },
    getPublishingStatisticsLicensorLimit(state) { return state.publishingStatisticsLicensorLimit },
    getPublishingStatisticsLicensorTotal(state) { return state.publishingStatisticsLicensorTotal },
    getPublishingStatisticsLicensorNumberOfPages(state) { return state.publishingStatisticsLicensorNumberOfPages },
    getPublishingStatisticsLicensorHasRun(state) { return state.publishingStatisticsLicensorHasRun },
    getPublishingStatisticsExportInProgress(state) { return state.publishingStatisticsExportInProgress },
}

const mutations = {
    setPublishingStatistics: (state, statistics) => { state.publishingStatistics = statistics },
    setPublishingStatisticsSearch: (state, search) => { state.publishingStatisticsSearch = search },
    setSelectedPublishingStatisticsRows: (state, value) => { state.selectedPublishingStatisticsRows = value },
    setSelectedPublishingStatisticsColumnTitles: (state, value) => { state.selectedPublishingStatisticsColumnTitles = value },
    setSelectedPublishingStatisticsOutputType: (state, value) => { state.selectedPublishingStatisticsOutputType = value },
    setSelectedPublishingStatisticsFrom: (state, value) => { state.selectedPublishingStatisticsFrom = value },
    setSelectedPublishingStatisticsTo: (state, value) => { state.selectedPublishingStatisticsTo = value },
    setSelectedPublishingStatisticsFromQuarter: (state, value) => { state.selectedPublishingStatisticsFromQuarter = value },
    setSelectedPublishingStatisticsToQuarter: (state, value) => { state.selectedPublishingStatisticsToQuarter = value },
    setSelectedPublishingStatisticsFromYear: (state, value) => { state.selectedPublishingStatisticsFromYear = value },
    setSelectedPublishingStatisticsToYear: (state, value) => { state.selectedPublishingStatisticsToYear = value },
    setSelectedPublishingStatisticsTypeOfRights: (state, values) => { state.selectedPublishingStatisticsTypeOfRights = values },

    setSelectedPublishingStatisticsRightsholder: (state, value) => { state.selectedPublishingStatisticsRightsholder = value },
    setSelectedPublishingStatisticsLicensor: (state, value) => { state.selectedPublishingStatisticsLicensor = value },
    setSelectedPublishingStatisticsContracts: (state, value) => { state.selectedPublishingStatisticsContracts = value },
    setSelectedPublishingStatisticsSources: (state, value) => { state.selectedPublishingStatisticsSources = value },
    setSelectedPublishingStatisticsRightsType: (state, value) => {
        state.selectedPublishingStatisticsRightsType = value
        state.selectedPublishingStatisticsTypeOfRights = value
    },
    setSelectedPublishingStatisticsLeftChart: (state, value) => { state.selectedPublishingStatisticsLeftChart = value },
    setSelectedPublishingStatisticsRightChart: (state, value) => { state.selectedPublishingStatisticsRightChart = value },
    setSelectedPublishingStatisticsDataTable: (state, value) => { state.selectedPublishingStatisticsDataTable = value },

    setSelectedPublishingStatisticsRegions: (state, regions) => { state.selectedPublishingStatisticsRegions = regions },
    setSelectedPublishingStatisticsRegionsInclude: (state, include) => { state.selectedPublishingStatisticsRegions.include = include },
    setSelectedPublishingStatisticsRegionsExclude: (state, exclude) => { state.selectedPublishingStatisticsRegions.exclude = exclude },
    setSelectedPublishingStatisticsRegionsString: (state, value) => { state.selectedPublishingStatisticsRegionsString = value },

    setPublishingStatisticsFavoriteDropdownValues: (state, values) => { state.publishingStatisticsFavoriteDropdownValues = values },
    setSelectedPublishingStatisticsFavorite: (state, value) => {
        if (value !== null && value.hasOwnProperty('value') && value.value === 'deleteFavorite') {
            state.tempSaveSelectedPublishingStatisticsFavorite = state.selectedPublishingStatisticsFavorite
        }
        state.selectedPublishingStatisticsFavorite = value
    },
    setShowPublishingStatisticsCreateNewFavoriteDialog: (state, show) => { state.showPublishingStatisticsCreateNewFavoriteDialog = show },
    setPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName: (state, name) => { state.publishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName = name },
    setTempSaveSelectedPublishingStatisticsFavorite: (state, selected) => { state.tempSaveSelectedPublishingStatisticsFavorite = selected },

    setPublishingStatisticsRightsholderOptions: (state, value) => { state.publishingStatisticsRightsholderOptions = value },
    setPublishingStatisticsDetailsRightsholder: (state, value) => {
        // Autocomplete items are { rightsholder, value }; keep that shape in details for v-model match.
        // selected* stays the bare A-Z entity for report/export/favorite save.
        if (value && value.rightsholder) {
            state.publishingStatisticsDetailsRightsholder = value
            state.selectedPublishingStatisticsRightsholder = value.rightsholder
        } else {
            state.publishingStatisticsDetailsRightsholder = value
            state.selectedPublishingStatisticsRightsholder = value
        }
    },
    setPublishingStatisticsLicensorOptions: (state, value) => { state.publishingStatisticsLicensorOptions = value },
    setPublishingStatisticsDetailsLicensor: (state, value) => {
        if (value && value.licensor) {
            state.publishingStatisticsDetailsLicensor = value
            state.selectedPublishingStatisticsLicensor = value.licensor
        } else {
            state.publishingStatisticsDetailsLicensor = value
            state.selectedPublishingStatisticsLicensor = value
        }
    },

    setShowPublishingStatisticsChartsSection: (state, value) => { state.showPublishingStatisticsChartsSection = value },
    setPublishingStatisticsCharts: (state, value) => { state.publishingStatisticsCharts = value },
    setPublishingStatisticsReportCards: (state, value) => { state.publishingStatisticsReportCards = value },
    setPublishingStatisticsFirstPieChartTitle: (state, value) => { state.publishingStatisticsFirstPieChartTitle = value },
    setPublishingStatisticsFirstPieChartSeriesTitle: (state, value) => { state.publishingStatisticsFirstPieChartSeriesTitle = value },
    setPublishingStatisticsFirstPieChartSeriesData: (state, value) => { state.publishingStatisticsFirstPieChartSeriesData = value },
    setPublishingStatisticsSecondPieChartTitle: (state, value) => { state.publishingStatisticsSecondPieChartTitle = value },
    setPublishingStatisticsSecondPieChartSeriesTitle: (state, value) => { state.publishingStatisticsSecondPieChartSeriesTitle = value },
    setPublishingStatisticsSecondPieChartSeriesData: (state, value) => { state.publishingStatisticsSecondPieChartSeriesData = value },
    setPublishingStatisticsCards: (state, value) => { state.publishingStatisticCards = value },
    setPublishingStatisticsDatatableData: (state, value) => { state.publishingStatisticsDatatableData = value },
    setPublishingStatisticsLicensorRows: (state, value) => { state.publishingStatisticsLicensorRows = value },
    setPublishingStatisticsLicensorPage: (state, value) => { state.publishingStatisticsLicensorPage = value },
    setPublishingStatisticsLicensorTotal: (state, value) => { state.publishingStatisticsLicensorTotal = value },
    setPublishingStatisticsLicensorNumberOfPages: (state, value) => { state.publishingStatisticsLicensorNumberOfPages = value },
    setPublishingStatisticsLicensorHasRun: (state, value) => { state.publishingStatisticsLicensorHasRun = value },
    setPublishingStatisticsExportInProgress: (state, value) => { state.publishingStatisticsExportInProgress = value },
}

const actions = {
    async fetchPublishingStatistics({ commit, getters }) {
        commit('setIsLoading', true)

        let fields = {
            fromQuarter: getters.getSelectedPublishingStatisticsFromQuarter,
            toQuarter: getters.getSelectedPublishingStatisticsToQuarter,
            fromYear: getters.getSelectedPublishingStatisticsFromYear,
            toYear: getters.getSelectedPublishingStatisticsToYear,
            typeOfRights: getters.getSelectedPublishingStatisticsTypeOfRights,
        }
        if (getters.getPublishingStatisticsSearch !== '') fields.search = getters.getPublishingStatisticsSearch

        let paramsString = this._vm.$alvPlugin.createApiUrlFilterString(fields)
        let response = await api.getListData(DIR, getters.getCurrentPage, paramsString)

        if (response === undefined || response.list.length === 0 || response.error === 1) {
            commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
            commit('setPublishingStatistics', [])
        } else {
            let allWorksHaveZeroRevenue = response.list.every((work) => {
                return (work.amountTotal === 0 || work.amountTotal === null || work.amountTotal === undefined) &&
                    (work.amountDomestic === 0 || work.amountDomestic === null || work.amountDomestic === undefined) &&
                    (work.amountForeign === 0 || work.amountForeign === null || work.amountForeign === undefined)
            })

            if (allWorksHaveZeroRevenue) {
                commit('setNoDataText', 'lang_PUBLISHING_STATISTICS_MESSAGE_WARNING_NO_REVENUES_FOUND')
                commit('setPublishingStatistics', [])
            } else {
                commit('setPublishingStatistics', response.list)
            }
        }

        this._vm.$alvPlugin.storePagingInformations(response.filteredRecords, response.currentPage, response.totalRecords)
        commit('setIsLoading', false)
    },

    async publishingStatisticReport({ getters, commit, rootGetters, dispatch }) {
        if (getters.getIsLoading) return

        if (getters.getSelectedPublishingStatisticsDataTable === 'byLicensor') {
            await dispatch('fetchPublishingStatisticsByLicensor', { page: 1, applyChartDefaults: true })
            return
        }

        commit('setShowPublishingStatisticsChartsSection', false)
        commit('setPublishingStatisticsDatatableData', { title: '', headers: [], data: [] })
        commit('setIsLoading', true)

        const typeOfRights = getters.getSelectedPublishingStatisticsRightsType
        const regions = getters.getSelectedPublishingStatisticsRegions

        let fields = {
            ...getPeriodApiFields(
                getters.getSelectedPublishingStatisticsFrom,
                getters.getSelectedPublishingStatisticsTo,
            ),
            typeOfRights,
            limit: 'none',
        }
        if (regions.include && regions.include.length > 0) {
            fields.regionInclude = regions.include
        }
        if (regions.exclude && regions.exclude.length > 0) {
            fields.regionExclude = regions.exclude
        }
        if (getters.getPublishingStatisticsSearch !== '') fields.search = getters.getPublishingStatisticsSearch
        if (getters.getSelectedPublishingStatisticsContracts.length > 0) {
            fields.contractTypes = getters.getSelectedPublishingStatisticsContracts
        }
        if (getters.getSelectedPublishingStatisticsSources.length > 0) {
            fields.sources = getters.getSelectedPublishingStatisticsSources
        }
        const rightsholder = getters.getSelectedPublishingStatisticsRightsholder
        if (rightsholder && rightsholder.alvId) {
            fields.rightsholder = rightsholder.alvId
        }

        let paramsString = this._vm.$alvPlugin.createApiUrlFilterString(fields)
        let response = await api.getListData(DIR, 1, paramsString)

        if (response === undefined || !response.list || response.list.length === 0 || response.error === 1) {
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND'),
            })
            commit('setPublishingStatisticsCharts', null)
            commit('setPublishingStatisticsReportCards', null)
            commit('setIsLoading', false)
            return
        }

        const allWorksHaveZeroRevenue = response.list.every((work) => {
            return !work.amountTotal && !work.amountDomestic && !work.amountForeign
        })

        if (allWorksHaveZeroRevenue) {
            commit('setSnackbar', {
                type: 'warning',
                msg: i18n.t('lang_PUBLISHING_STATISTICS_MESSAGE_WARNING_NO_REVENUES_FOUND'),
            })
            commit('setPublishingStatisticsCharts', null)
            commit('setPublishingStatisticsReportCards', null)
            commit('setIsLoading', false)
            return
        }

        commitPublishingStatisticsDisplay(
            commit,
            getters,
            rootGetters,
            response.list,
            response.charts,
            response.cards,
        )
        commit('setPublishingStatistics', response.list)
        commit('setPublishingStatisticsCharts', response.charts || null)
        commit('setPublishingStatisticsReportCards', response.cards || null)
        commit('setShowPublishingStatisticsChartsSection', true)
        commit('setIsLoading', false)
    },

    async fetchPublishingStatisticsByLicensor({ commit, getters }, payload) {
        const page = (payload && payload.page) ? payload.page : 1
        const limit = LICENSOR_PAGE_LIMIT
        const applyChartDefaults = !!(payload && payload.applyChartDefaults)

        commit('setIsLoading', true)
        commit('setPublishingStatisticsLicensorPage', page)

        const body = buildLicensorRequestBody(getters, { page, limit })
        const licensorId = body.licensorId

        if (applyChartDefaults) {
            commit('setSelectedPublishingStatisticsLeftChart', licensorId ? 'byWork' : 'byLicensor')
            if (!LICENSOR_CHART_KEYS.includes(getters.getSelectedPublishingStatisticsRightChart)) {
                commit('setSelectedPublishingStatisticsRightChart', 'byRightsType')
            }
        }

        const response = await api.triggerAction('publishing/statistics/byLicensor', body, {})
        commit('setPublishingStatisticsLicensorHasRun', true)

        const payloadRes = (response && response.response) ? response.response : response
        const licensors = (payloadRes && payloadRes.licensors) || []
        const ok = response && response.error === -1

        if (!ok) {
            const errorMsg = (payloadRes && payloadRes.errorMsg) || (response && response.errorMsg)
            commit('setPublishingStatisticsLicensorRows', [])
            commit('setPublishingStatisticsLicensorTotal', 0)
            commit('setPublishingStatisticsLicensorNumberOfPages', 0)
            commitLicensorDisplay(commit, getters, [])
            commit('setSnackbar', {
                type: 'error',
                msg: errorMsg && String(errorMsg).indexOf('lang_') === 0
                    ? i18n.t(errorMsg)
                    : (errorMsg || i18n.t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')),
            })
            commit('setIsLoading', false)
            return
        }

        const rows = mapLicensorRows(licensors)
        commit('setPublishingStatisticsLicensorRows', rows)
        commit('setPublishingStatisticsLicensorPage', payloadRes.page || page)
        commit('setPublishingStatisticsLicensorTotal', Number(payloadRes.total) || 0)
        commit('setPublishingStatisticsLicensorNumberOfPages', Number(payloadRes.numberOfPages) || 0)
        commitLicensorDisplay(commit, getters, rows)
        commit('setIsLoading', false)
    },

    refreshPublishingStatisticsDisplay({ getters, commit, rootGetters }) {
        const list = getters.getPublishingStatistics
        const charts = getters.getPublishingStatisticsCharts
        const cards = getters.getPublishingStatisticsReportCards
        if (!getters.getShowPublishingStatisticsChartsSection || !list || list.length === 0) return
        commitPublishingStatisticsDisplay(
            commit,
            getters,
            rootGetters,
            list,
            charts,
            cards,
            { skipDatatable: getters.getSelectedPublishingStatisticsDataTable === 'byLicensor' },
        )
    },

    refreshLicensorDisplay({ getters, commit }) {
        if (!getters.getPublishingStatisticsLicensorHasRun) return
        commitLicensorDisplay(commit, getters, getters.getPublishingStatisticsLicensorRows || [])
    },

    async fetchAutocompletePublishingStatisticsRightsholder({ commit }, input) {
        let response = await api.getAutocompleteSearch('az/persons', input)
        const options = (response.list || []).map((entry) => ({
            rightsholder: entry,
            value: entry.id,
        }))
        commit('setPublishingStatisticsRightsholderOptions', options)
    },

    async fetchAutocompletePublishingStatisticsLicensor({ commit }, input) {
        const [persons, groups] = await Promise.all([
            api.getAutocompleteSearch('az/persons', input),
            api.getAutocompleteSearch('az/groups', input),
        ])
        const toOptions = (list) => (list || []).map((entry) => ({
            licensor: entry,
            value: entry.alvId || entry.id,
        }))
        commit('setPublishingStatisticsLicensorOptions', [
            ...toOptions(persons && persons.list),
            ...toOptions(groups && groups.list),
        ])
    },

    async publishingStatisticsExportExcel({ getters, commit }) {
        if (getters.getPublishingStatisticsExportInProgress || getters.getIsLoading) return

        commit('setPublishingStatisticsExportInProgress', true)
        try {
            let response
            if (getters.getSelectedPublishingStatisticsDataTable === 'byLicensor') {
                response = await api.triggerAction(
                    'publishing/statistics/byLicensor',
                    buildLicensorRequestBody(getters),
                    { action: 'export' },
                )
            } else {
                let queryParams = {
                    action: 'export',
                    type: 'excel',
                }

                let metaData = {
                    ...getPeriodApiFields(
                        getters.getSelectedPublishingStatisticsFrom,
                        getters.getSelectedPublishingStatisticsTo,
                    ),
                    typeOfRights: getters.getSelectedPublishingStatisticsRightsType,
                    rows: getters.getSelectedPublishingStatisticsRows,
                    columnTitles: getters.getSelectedPublishingStatisticsColumnTitles,
                    outputType: getters.getSelectedPublishingStatisticsOutputType,
                    search: getters.getPublishingStatisticsSearch,
                    contracts: getters.getSelectedPublishingStatisticsContracts,
                    sources: getters.getSelectedPublishingStatisticsSources,
                    rightsType: getters.getSelectedPublishingStatisticsRightsType,
                    rightsholder: getters.getSelectedPublishingStatisticsRightsholder,
                    regionInclude: getters.getSelectedPublishingStatisticsRegions.include,
                    regionExclude: getters.getSelectedPublishingStatisticsRegions.exclude,
                    leftChart: getters.getSelectedPublishingStatisticsLeftChart,
                    rightChart: getters.getSelectedPublishingStatisticsRightChart,
                    dataTable: getters.getSelectedPublishingStatisticsDataTable,
                }

                response = await api.triggerAction(DIR, metaData, queryParams)
            }
            if (response.error === -1) {
                api.getFileFromALV(response.response.path)
                commit('setSnackbar', {
                    type: 'success',
                    msg: i18n.t('lang_GENERAL_SNACKBAR_DOWNLOAD_SUCCESSFULL'),
                })
            } else {
                commit('setSnackbar', {
                    type: 'error',
                    msg: i18n.t(response.response.errorMsg),
                })
            }
        } finally {
            commit('setPublishingStatisticsExportInProgress', false)
        }
    },

    async fetchPublishingStatisticsFavoriteDropdownValues({ commit, getters }) {
        let filters = await api.getDropdownValues(DIR, 'favourites')
        if (!Array.isArray(filters)) filters = []
        let selectedFavourite = getters.getSelectedPublishingStatisticsFavorite
        let favorites = []

        if (selectedFavourite === null) {
            favorites.push({
                text: 'lang_LABEL_STATISTIC_ADD_NEW_FILTER',
                value: 'newFavorite',
            })
        }

        if (selectedFavourite !== null && selectedFavourite.hasOwnProperty('id')) {
            favorites.push({
                text: 'lang_LABEL_STATISTIC_DELETE_FILTER',
                value: 'deleteFavorite',
            })
        }

        if (filters.length !== 0) {
            favorites.sort((a, b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            favorites.push({ divider: true })
            filters.forEach((element) => {
                element.alvqa = 'publishing-statistics-list-favorit-dropdown-' + element.id
                favorites.push(element)
            })
        }

        commit('setPublishingStatisticsFavoriteDropdownValues', favorites)
    },

    async doPublishingStatisticsFavorite({ getters, commit, dispatch }) {
        dispatch('fetchPublishingStatisticsFavoriteDropdownValues')
        let selectedFavourite = getters.getSelectedPublishingStatisticsFavorite

        if (selectedFavourite === null) return

        if (selectedFavourite.hasOwnProperty('value')) {
            if (selectedFavourite.value === 'newFavorite') {
                commit('setShowPublishingStatisticsCreateNewFavoriteDialog', true)
            }
            if (selectedFavourite.value === 'deleteFavorite') {
                commit('setDeletionDialogContext', 'TempSaveSelectedPublishingStatisticsFavorite')
                commit('setShowDeletionDialog', true)
                commit('setSelectedPublishingStatisticsFavorite', null)
            }
            return
        }

        commit('setSelectedPublishingStatisticsFrom', selectedFavourite.monthFrom)
        commit('setSelectedPublishingStatisticsTo', selectedFavourite.monthTo)
        commit('setPublishingStatisticsSearch', selectedFavourite.search || '')
        commit('setSelectedPublishingStatisticsRightsType', selectedFavourite.rightsType || [])
        commit('setSelectedPublishingStatisticsSources', selectedFavourite.sources || [])
        commit('setSelectedPublishingStatisticsContracts', selectedFavourite.contracts || [])
        commit('setSelectedPublishingStatisticsLeftChart', normalizeChartKey(selectedFavourite.leftChart) || 'bySources')
        commit('setSelectedPublishingStatisticsRightChart', normalizeChartKey(selectedFavourite.rightChart) || 'byType')
        commit('setSelectedPublishingStatisticsDataTable', selectedFavourite.dataTable)

        if (selectedFavourite.rightsholder) {
            const person = await resolvePublishingStatisticsRightsholder(selectedFavourite.rightsholder)
            if (person) {
                const option = {
                    rightsholder: person,
                    value: person.id,
                }
                commit('setPublishingStatisticsRightsholderOptions', [option])
                commit('setPublishingStatisticsDetailsRightsholder', option)
            } else {
                commit('setPublishingStatisticsDetailsRightsholder', null)
                commit('setPublishingStatisticsRightsholderOptions', [])
            }
        } else {
            commit('setPublishingStatisticsDetailsRightsholder', null)
            commit('setPublishingStatisticsRightsholderOptions', [])
        }

        if (selectedFavourite.region) {
            const regionsString = this._vm.$alvPlugin.createRegionsToString(selectedFavourite.region)
            commit('setRegionDialogDetailsInclude', selectedFavourite.region.include)
            commit('setRegionDialogDetailsExclude', selectedFavourite.region.exclude)
            commit('setSelectedPublishingStatisticsRegions', selectedFavourite.region)
            commit('setSelectedPublishingStatisticsRegionsString', regionsString)
        } else {
            commit('setSelectedPublishingStatisticsRegions', { include: [], exclude: [] })
            commit('setSelectedPublishingStatisticsRegionsString', '')
        }
    },

    async saveNewPublishingStatisticsFavorite({ getters, commit, dispatch }) {
        const favoriteName = String(
            getters.getPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName == null
                ? ''
                : getters.getPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName
        ).trim()
        if (!favoriteName) {
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t('lang_GENERIC_SNACKBAR_ADDING_WAS_NOT_SUCCESSFUL'),
            })
            return false
        }

        commit('setPublishingStatisticsCreateNewFavoriteDialogDetailsFavoriteName', favoriteName)
        await dispatch('fetchPublishingStatisticsFavoriteDropdownValues')
        if (isStatisticsFavoriteNameTaken(
            getters.getPublishingStatisticsFavoriteDropdownValues,
            favoriteName
        )) {
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t('lang_GENERAL_STATISTICS_VALIDATION_FAVORITE_NAME_EXISTS'),
            })
            return false
        }

        let queryParams = { action: 'addFavorite' }
        let metaData = buildPublishingStatisticsFavoritePayload(getters)

        let response = await api.triggerAction(DIR, metaData, queryParams)
        if (response && response.error === -1) {
            dispatch('fetchPublishingStatisticsFavoriteDropdownValues')
            commit('setSnackbar', {
                type: 'success',
                msg: i18n.t('lang_GENERIC_SNACKBAR_ADDING_WAS_SUCCESSFUL'),
            })
            commit('setSelectedPublishingStatisticsFavorite', response.response.newFilter)
            return true
        } else {
            const errorMsg = response && response.response && response.response.errorMsg
                ? response.response.errorMsg
                : 'lang_GENERIC_SNACKBAR_ADDING_WAS_NOT_SUCCESSFUL'
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t(errorMsg),
            })
            return false
        }
    },

    async deleteTempSaveSelectedPublishingStatisticsFavorite({ getters, commit, dispatch }) {
        let selectedFavoriteId = getters.getTempSaveSelectedPublishingStatisticsFavorite.id
        let response = await api.deleteEntries(
            DIR + '/favourites?action=deleteFavorite',
            { id: selectedFavoriteId }
        )

        if (response.error === -1) {
            commit('setTempSaveSelectedPublishingStatisticsFavorite', [])
            dispatch('fetchPublishingStatisticsFavoriteDropdownValues')
            commit('setSnackbar', {
                type: 'success',
                msg: i18n.t('lang_GENERIC_SNACKBAR_DELETE_SUCCESSFUL'),
            })
        } else {
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t('lang_GENERIC_SNACKBAR_DELETE_NOT_SUCCESSFUL'),
            })
        }
    },
}

export default {
    state, getters, actions, mutations,
}
